import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "antd";

import { responsive } from "../../utils/style";
import { copyTextToClipboard } from "../../utils/copyToClipboard";
import { isAdmin, isStaff, isStudent } from "../../utils/role";
import { onLoadProfile } from "../../stores/editProfile";
import { parseDateToString } from "../../utils/date";
import NextMatchForm from "./NextMatchForm";

import ICON from "../../assets/icons/copy.svg";
import STAR from "../../assets/icons/star.svg";

const Profile = () => {
  const dispatch = useDispatch();
  const { profile } = useSelector((state) => state.editProfile);
  const { role = [] } = useSelector((s) => s.auth.userInfo);
  const [editingNextMatch, setEditingNextMatch] = useState(false);

  useEffect(() => {
    dispatch(onLoadProfile());
  }, []);

  if (editingNextMatch) {
    return (
      <NextMatchForm
        endMatchDate={profile.matchList[0]?.endDate}
        onSuccess={() => setEditingNextMatch(false)}
      />
    );
  }

  return (
    <Container>
      <Background>
        <ProfileWrapper>
          <TopicWrapper>
            <Topic>โปรไฟล์</Topic>
            {isAdmin(role) && (
              <Badge>
                <img src={STAR} />
                {` `}
                หัวหน้าโค้ช
              </Badge>
            )}
          </TopicWrapper>
          {isStudent(role) && (
            <Section>
              <GroupInfoHalf>
                <Label>ชื่อ (ชื่อเล่น)</Label>
                <Value>{`${profile.firstname} (${profile.nickname})`}</Value>
              </GroupInfoHalf>
              <GroupInfoHalf>
                <Label>ชั้น</Label>
                <Value>{`ม.${profile.classNo} ห้อง ${profile.roomNo}`}</Value>
              </GroupInfoHalf>
            </Section>
          )}
          {isStaff(role) && (
            <Section>
              <GroupInfoFull>
                <Label>ชื่อ</Label>
                <Value>{profile.firstname}</Value>
              </GroupInfoFull>
            </Section>
          )}
          <Section>
            <GroupInfoFull>
              <Label>กลุ่ม</Label>
              <Value>{profile.schoolName}</Value>
            </GroupInfoFull>
          </Section>
          <Section>
            <GroupInfoFull>
              <Label>ชื่อผุ้ใช้งาน (USERNAME)</Label>
              <Value>{profile.username}</Value>
            </GroupInfoFull>
          </Section>
          {isAdmin(role) && (
            <>
              <Section>
                <GroupInfoFull>
                  <Label>รหัสสำหรับสามเณร</Label>
                  <Value>{profile.studentSecretCode}</Value>
                </GroupInfoFull>
                <CopyIcon
                  src={ICON}
                  onClick={() => copyTextToClipboard(profile.studentSecretCode)}
                />
              </Section>
              <Section>
                <GroupInfoFull>
                  <Label>รหัสสำหรับพระสงฆ์ ครูฆราวาส</Label>
                  <Value>{profile.staffSecretCode}</Value>
                </GroupInfoFull>
                <CopyIcon
                  src={ICON}
                  onClick={() => copyTextToClipboard(profile.staffSecretCode)}
                />
              </Section>
            </>
          )}
          {isAdmin(role) && !profile.matchList[1] && (
            <Section>
              <GroupInfoFull>
                <Label>ชื่อกิจกรรมในการแข่งขัน</Label>
                <Value>{profile.matchList[0]?.name}</Value>
              </GroupInfoFull>
            </Section>
          )}
          {isAdmin(role) && (
            <>
              <Section>
                <GroupInfoFull>
                  <Label>จำนวนสัปดาห์ที่แข่งขัน</Label>
                  <Value>{profile.matchList[0]?.maxWeek}</Value>
                </GroupInfoFull>
              </Section>
              <Section>
                <GroupInfoFull>
                  <Label>วันเริ่มต้นของการแข่งขัน</Label>
                  <Value>
                    {parseDateToString(profile.matchList[0]?.startDate)}
                  </Value>
                </GroupInfoFull>
              </Section>
            </>
          )}
          {isAdmin(role) && profile.matchList[1] && (
            <>
              <Section>
                <GroupInfoFull>
                  <Label>วันเริ่มต้นของการแข่งขันเทอม 2</Label>
                  <Value>
                    {parseDateToString(profile.matchList[1]?.startDate)}
                  </Value>
                </GroupInfoFull>
              </Section>
              <Section>
                <GroupInfoFull>
                  <Label>จำนวนสัปดาห์ที่แข่งขันเทอม 2</Label>
                  <Value>{profile.matchList[1]?.maxWeek}</Value>
                </GroupInfoFull>
              </Section>
            </>
          )}
          <ActionGroup>
            {isAdmin(role) && !profile.matchList[1] && (
              <ActionWrapper>
                <NextMatchButton onClick={() => setEditingNextMatch(true)}>
                  กำหนดวันเริ่มการแข่งขันของเกมถัดไป
                </NextMatchButton>
              </ActionWrapper>
            )}
            <ActionWrapper>
              <ButtonStyled to="/changePassword">เปลี่ยนรหัสผ่าน</ButtonStyled>
            </ActionWrapper>
            <ActionWrapper>
              <ButtonStyled to="/profile/edit">แก้ไขโปรไฟล์</ButtonStyled>
            </ActionWrapper>
          </ActionGroup>
        </ProfileWrapper>
      </Background>
    </Container>
  );
};

export default Profile;

const Container = styled.div`
  font-family: Noto Sans Thai, Noto Sans;
  background-color: #fffbeb;

  input {
    border-radius: 4px;
    height: 52px;
    font-size: 18px;
  }
  .ant-input-affix-wrapper {
    font-size: 18px;
  }
`;
const Background = styled.div`
  min-height: 100vh;
  max-width: 1144px;
  background-color: white;
  margin: auto;
  padding: 40px 0;

  ${responsive.tablet(css`
    max-width: unset;
  `)}
  ${responsive.mobile(css`
    padding: 40px 16px;
  `)}
`;
const ProfileWrapper = styled.div`
  max-width: 440px;
  margin: auto;
`;
const Topic = styled.h4`
  font-weight: 600;
  font-size: 23px;
  line-height: 28px;
  margin: 0;
  align-self: flex-end;
`;
const Section = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
`;
const GroupInfoHalf = styled.div`
  flex: 0.45;
`;
const GroupInfoFull = styled.div`
  flex: 0.8;

  ${responsive.mobile(css`
    flex: 1;
  `)}
`;
const Label = styled.label`
  font-weight: 700;
  font-size: 16px;
  line-height: 26px;
  color: #4b5768;
`;
const Value = styled.div`
  font-weight: 400;
  font-size: 18px;
  line-height: 26px;
  color: #4b5768;
  border-bottom: 1px solid #e7eaee;
`;
const ActionGroup = styled.div`
  margin-top: 40px;
`;
const ActionWrapper = styled.div`
  display: flex;
  text-align: center;
  justify-content: center;
  margin-top: 16px;

  ${responsive.mobile(css`
    margin-top: 24px;
  `)}
`;
const ButtonStyled = styled(Link)`
  flex: 1;
  background: #b5f6df;
  border: 1px solid #16af7b;
  border-radius: 100px;
  padding: 14px 32px;
  font-weight: 600;
  font-size: 19px;
  line-height: 23px;
  letter-spacing: -0.02em;
  color: #16af7b;
  max-width: 206px;
  :hover {
    color: #16af7b;
  }
`;
const CopyIcon = styled.img`
  :hover {
    cursor: pointer;
  }
`;
const NextMatchButton = styled.button`
  padding: 14px 32px;
  width: 100%;
  height: 51px;
  background: #16af7b;
  border-radius: 100px;
  font-weight: 700;
  font-size: 19px;
  line-height: 23px;
  letter-spacing: -0.02em;
  color: #ffffff;
  border: none;
  :hover {
    cursor: pointer;
  }
`;
const Badge = styled.div`
  padding: 4px 16px;
  width: 135px;
  height: 34px;
  background: #058459;
  border-radius: 8px;
  font-weight: 700;
  font-size: 16px;
  line-height: 26px;
  color: #ffffff;

  img {
    margin-right: 5px;
  }
`;
const TopicWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 40px;
`;
